import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, Gauge, Sparkles, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { useToast } from '@/hooks/use-toast';
import { getMetaApps } from '@/services/api';
import {
    getBroadcastV2Settings,
    updateBroadcastV2Settings,
    listAppRateLimits,
    upsertAppRateLimit,
    bulkUpsertAppRateLimits,
    deleteAppRateLimit,
} from '@/services/api/connections';

// Limite considerado baixo o suficiente para achatar throughput de um
// broadcast grande — só um aviso, não bloqueia (a decisão é do usuário).
const LOW_LIMIT_THRESHOLD = 20;

/**
 * Config do Broadcast V2 (esteira "leads is_active=1" + rate limit por app),
 * por usuário. Antes disso os dois valores eram hardcoded no código
 * (only_active=True + META_APP_RATE_LIMIT_PER_SEC de env, igual pra todo mundo).
 */
export default function BroadcastV2SettingsPanel() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [draftAppIds, setDraftAppIds] = useState<string[]>([]);
    const [draftMaxPerSec, setDraftMaxPerSec] = useState('');
    const [editingAppId, setEditingAppId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');

    const { data: settings, isLoading: loadingSettings } = useQuery({
        queryKey: ['broadcast-v2-settings'],
        queryFn: getBroadcastV2Settings,
    });

    const { data: rateLimitsData, isLoading: loadingLimits } = useQuery({
        queryKey: ['broadcast-v2-rate-limits'],
        queryFn: listAppRateLimits,
    });

    const { data: apps = [] } = useQuery({
        queryKey: ['meta-apps-all'],
        queryFn: () => getMetaApps('ativos'),
    });

    const toggleMutation = useMutation({
        mutationFn: updateBroadcastV2Settings,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['broadcast-v2-settings'] });
            toast({ title: 'Configuração salva' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível salvar a configuração.' });
        },
    });

    const bulkAddMutation = useMutation({
        mutationFn: ({ appIds, maxPerSec }: { appIds: number[]; maxPerSec: number }) =>
            bulkUpsertAppRateLimits(appIds, maxPerSec),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['broadcast-v2-rate-limits'] });
            toast({
                title: variables.appIds.length > 1 ? 'Limites salvos' : 'Limite salvo',
                description: variables.appIds.length > 1 ? `${variables.appIds.length} apps configurados.` : undefined,
            });
            setDraftAppIds([]);
            setDraftMaxPerSec('');
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível salvar o(s) limite(s).' });
        },
    });

    const editMutation = useMutation({
        mutationFn: ({ appId, maxPerSec }: { appId: number; maxPerSec: number }) =>
            upsertAppRateLimit(appId, maxPerSec),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['broadcast-v2-rate-limits'] });
            toast({ title: 'Limite atualizado' });
            setEditingAppId(null);
            setEditValue('');
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível atualizar o limite.' });
        },
    });

    const deleteLimitMutation = useMutation({
        mutationFn: deleteAppRateLimit,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['broadcast-v2-rate-limits'] });
            toast({ title: 'Limite removido', description: 'Esse app volta a usar o padrão do sistema.' });
        },
    });

    const configuredAppIds = new Set((rateLimitsData?.limits ?? []).map(l => l.app_id));
    const appOptions = apps
        .filter(a => !configuredAppIds.has(a.id))
        .map(a => ({
            value: String(a.id),
            label: a.app_name,
            sublabel: a.is_active ? undefined : 'App desativado',
        }));

    const handleAddLimits = () => {
        const appIds = draftAppIds.map(Number).filter(Boolean);
        const maxPerSec = Number(draftMaxPerSec);
        if (appIds.length === 0 || !maxPerSec || maxPerSec < 1) return;
        bulkAddMutation.mutate({ appIds, maxPerSec });
    };

    const startEditing = (appId: number, currentValue: number) => {
        setEditingAppId(appId);
        setEditValue(String(currentValue));
    };

    const cancelEditing = () => {
        setEditingAppId(null);
        setEditValue('');
    };

    const saveEditing = (appId: number) => {
        const maxPerSec = Number(editValue);
        if (!maxPerSec || maxPerSec < 1) return;
        editMutation.mutate({ appId, maxPerSec });
    };

    const isLoading = loadingSettings || loadingLimits;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Config</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    Configurações do pipeline de envio V2: base de leads considerada e limite de envios por segundo por app.
                </p>
            </div>

            {/* Base limpa / suja */}
            <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0f172a]/5 text-[#0f172a]">
                            <Sparkles className="h-4 w-4" />
                        </span>
                        <div>
                            <p className="text-sm font-medium text-gray-900">
                                Enviar apenas para leads ativos (base limpa)
                            </p>
                            <p className="text-xs text-gray-500 mt-1 max-w-md">
                                Ligado: broadcasts V2 enviam só para leads com status ativo (is_active=1).
                                Desligado (padrão): envia para toda a base, inclusive leads já desativados por erro anterior.
                            </p>
                        </div>
                    </div>
                    <Switch
                        checked={settings?.active_leads_only ?? false}
                        disabled={toggleMutation.isPending}
                        onCheckedChange={(checked) => toggleMutation.mutate(checked)}
                    />
                </div>
            </div>

            {/* Limite de envios por app */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0f172a]/5 text-[#0f172a]">
                        <Gauge className="h-4 w-4" />
                    </span>
                    <div>
                        <p className="text-sm font-medium text-gray-900">Limite de envios por segundo, por app</p>
                        <p className="text-xs text-gray-500 mt-1">
                            Apps sem limite configurado usam o padrão do sistema ({rateLimitsData?.default_max_per_sec ?? 100}/s).
                        </p>
                    </div>
                </div>

                {rateLimitsData && rateLimitsData.limits.length > 0 && (
                    <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                        {rateLimitsData.limits.map((limit) => {
                            const isEditing = editingAppId === limit.app_id;
                            const isLow = limit.max_per_sec < LOW_LIMIT_THRESHOLD;
                            const appDisabled = limit.app_is_active === false;
                            return (
                                <div key={limit.app_id} className="flex items-center justify-between gap-3 px-4 py-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="truncate text-sm font-medium text-gray-900">
                                                {limit.app_name ?? `App #${limit.app_id}`}
                                            </p>
                                            {appDisabled && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    App desativado
                                                </span>
                                            )}
                                        </div>
                                        {isLow && !appDisabled && (
                                            <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-600">
                                                <AlertTriangle className="h-3 w-3" />
                                                Limite baixo — pode reduzir bastante o throughput deste app
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        {isEditing ? (
                                            <>
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    max={1000}
                                                    autoFocus
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') saveEditing(limit.app_id);
                                                        if (e.key === 'Escape') cancelEditing();
                                                    }}
                                                    className="h-8 w-20 text-sm"
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                                    onClick={() => saveEditing(limit.app_id)}
                                                    disabled={editMutation.isPending}
                                                    aria-label="Salvar"
                                                >
                                                    <Check className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                                                    onClick={cancelEditing}
                                                    aria-label="Cancelar"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <span className={`text-sm font-semibold tabular-nums ${isLow ? 'text-amber-600' : 'text-gray-900'}`}>
                                                    {limit.max_per_sec}/s
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                                                    onClick={() => startEditing(limit.app_id, limit.max_per_sec)}
                                                    aria-label={`Editar limite de ${limit.app_name ?? limit.app_id}`}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => deleteLimitMutation.mutate(limit.app_id)}
                                                    aria-label={`Remover limite de ${limit.app_name ?? limit.app_id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="border-t border-gray-100 pt-4 space-y-3">
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                                Apps
                            </label>
                            <SearchableMultiSelect
                                values={draftAppIds}
                                onValuesChange={setDraftAppIds}
                                options={appOptions}
                                placeholder="Selecione um ou mais apps..."
                                searchPlaceholder="Buscar app..."
                                showTags={false}
                                selectedLabel={(n) => `${n} app${n > 1 ? 's' : ''} selecionado${n > 1 ? 's' : ''}`}
                            />
                        </div>
                        <div className="w-32">
                            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                                Envios/s
                            </label>
                            <Input
                                type="number"
                                min={1}
                                max={1000}
                                value={draftMaxPerSec}
                                onChange={(e) => setDraftMaxPerSec(e.target.value)}
                                placeholder="100"
                            />
                        </div>
                        <Button
                            size="sm"
                            className="gap-1.5"
                            disabled={draftAppIds.length === 0 || !draftMaxPerSec || bulkAddMutation.isPending}
                            onClick={handleAddLimits}
                        >
                            {bulkAddMutation.isPending ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <Plus className="w-3.5 h-3.5" />
                            )}
                            Adicionar
                        </Button>
                    </div>

                    {draftAppIds.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {draftAppIds.map((id) => {
                                const opt = appOptions.find((o) => o.value === id);
                                return (
                                    <span
                                        key={id}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 pl-2.5 pr-1.5 py-1 text-xs font-medium text-blue-700"
                                    >
                                        {opt?.label ?? `App #${id}`}
                                        <button
                                            type="button"
                                            onClick={() => setDraftAppIds(draftAppIds.filter((v) => v !== id))}
                                            className="hover:text-blue-900"
                                            aria-label={`Remover ${opt?.label ?? id}`}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {Number(draftMaxPerSec) > 0 && Number(draftMaxPerSec) < LOW_LIMIT_THRESHOLD && (
                        <p className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {draftMaxPerSec}/s é um limite baixo — pode reduzir bastante o throughput dos apps selecionados.
                        </p>
                    )}
                </div>

                {appOptions.length === 0 && configuredAppIds.size > 0 && (
                    <p className="text-xs text-gray-400">Todos os apps já têm limite configurado.</p>
                )}
            </div>
        </div>
    );
}
